import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    ApiOutlined,
    AuditOutlined,
    BankOutlined,
    CheckCircleOutlined,
    FileProtectOutlined,
    FileTextOutlined,
    MobileOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    SolutionOutlined,
    TeamOutlined,
    LockOutlined,
    CloudUploadOutlined,
    NodeIndexOutlined,
    EditOutlined,
    DatabaseOutlined,
    KeyOutlined,
    UserSwitchOutlined
} from '@ant-design/icons';
import { getKedoToken } from './kedoApi';
import './KedoShell.css';

const scenarios = [
    {
        title: 'С контрагентами',
        text: 'Исходящие и входящие: отправьте акт или УПД, получите подпись, зафиксируйте отказ или аннулирование.',
        icon: <SendOutlined />
    },
    {
        title: 'Внутри компании',
        text: 'Маршруты согласования и подписания: последовательные, параллельные и смешанные этапы с дедлайнами.',
        icon: <NodeIndexOutlined />
    },
    {
        title: 'Кадровый КЭДО',
        text: 'Приказы, заявления и ознакомления сотрудников — с ПЭП по SMS или квалифицированной подписью.',
        icon: <TeamOutlined />
    }
];

const docTypes = [
    { label: 'Договоры и допсоглашения', icon: <FileTextOutlined /> },
    { label: 'Акты и счета', icon: <AuditOutlined /> },
    { label: 'УПД и счета-фактуры', icon: <BankOutlined /> },
    { label: 'Кадровые приказы', icon: <SolutionOutlined /> },
    { label: 'Произвольные PDF и XML', icon: <FileProtectOutlined /> }
];

const signWays = [
    {
        title: 'ПЭП по SMS',
        text: 'Простая электронная подпись для физлиц и внутренних документов — код в сообщении, протокол в журнале.',
        icon: <MobileOutlined />
    },
    {
        title: 'Госключ',
        text: 'Мобильное подписание УНЭП/УКЭП без токена на компьютере — удобно для выездных и удалённых сотрудников.',
        icon: <SafetyCertificateOutlined />
    },
    {
        title: 'УКЭП и МЧД',
        text: 'Квалифицированная подпись и машиночитаемые доверенности — юридическая сила как у бумажного оригинала.',
        icon: <KeyOutlined />
    }
];

const steps = [
    {
        n: '01',
        title: 'Загрузите документ',
        text: 'PDF или XML, метаданные и ссылки на внешние системы.',
        icon: <CloudUploadOutlined />
    },
    {
        n: '02',
        title: 'Назначьте маршрут',
        text: 'Согласующие и подписанты, сроки, эскалации.',
        icon: <NodeIndexOutlined />
    },
    {
        n: '03',
        title: 'Подпишите',
        text: 'SMS, Госключ или УКЭП — пакет с протоколом и штампом.',
        icon: <EditOutlined />
    },
    {
        n: '04',
        title: 'Храните и обменивайтесь',
        text: 'Неизменяемый архив, webhooks и API для интеграций.',
        icon: <DatabaseOutlined />
    }
];

const trustItems = [
    { label: 'Своя регистрация и кабинет организации', icon: <UserSwitchOutlined /> },
    { label: 'API-ключи и OpenAPI для интеграций', icon: <ApiOutlined /> },
    { label: 'Роли: владелец, админ, подписант, наблюдатель', icon: <TeamOutlined /> },
    { label: 'Неизменяемый протокол подписания', icon: <LockOutlined /> }
];

const KedoLandingPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        if (getKedoToken()) navigate('/kedo/app', { replace: true });
    }, [navigate]);

    return (
        <div className="kedo-landing">
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,600;8..60,700&display=swap"
            />

            <header className="kedo-landing-nav">
                <Link to="/kedo" className="kedo-brand">
                    <span className="kedo-brand-mark">Kedo</span>
                    <span className="kedo-brand-sub">КЭДО / ЭДО</span>
                </Link>
                <nav className="kedo-landing-links">
                    <a href="#scenarios">Сценарии</a>
                    <a href="#sign">Подписи</a>
                    <a href="#how">Как работает</a>
                    <a href="/api/kedo/v1/docs" target="_blank" rel="noreferrer">
                        API
                    </a>
                </nav>
                <div className="kedo-cta-row kedo-cta-nav">
                    <Link className="kedo-btn kedo-btn-ghost" to="/kedo/login">
                        Войти
                    </Link>
                    <Link className="kedo-btn kedo-btn-primary" to="/kedo/register">
                        Подключить
                    </Link>
                </div>
            </header>

            <section className="kedo-landing-hero">
                <div className="kedo-landing-hero-copy">
                    <p className="kedo-eyebrow">Электронный документооборот</p>
                    <h1>Обменивайтесь и подписывайте документы без бумаги</h1>
                    <p className="lede">
                        Kedo — платформа ЭДО и кадрового КЭДО: договоры, акты,
                        УПД и приказы с юридически значимой подписью.
                    </p>
                    <div className="kedo-cta-row">
                        <Link
                            className="kedo-btn kedo-btn-primary"
                            to="/kedo/register"
                        >
                            Попробовать бесплатно
                        </Link>
                        <Link
                            className="kedo-btn kedo-btn-ghost"
                            to="/kedo/login"
                        >
                            Войти в кабинет
                        </Link>
                    </div>
                </div>
                <div className="kedo-landing-hero-visual" aria-hidden="true">
                    <div className="kedo-doc-stack">
                        <div className="kedo-doc-sheet kedo-doc-back">
                            <FileTextOutlined className="kedo-doc-ico" />
                            Акт №148
                        </div>
                        <div className="kedo-doc-sheet kedo-doc-mid">
                            <CheckCircleOutlined className="kedo-doc-ico" />
                            УПД · подписан
                        </div>
                        <div className="kedo-doc-sheet kedo-doc-front">
                            <span className="kedo-doc-stamp">
                                <SafetyCertificateOutlined /> УКЭП
                            </span>
                            Договор поставки
                        </div>
                    </div>
                </div>
            </section>

            <section className="kedo-landing-band" id="scenarios">
                <div className="kedo-landing-inner">
                    <h2>Один сервис — для контрагентов и сотрудников</h2>
                    <p className="section-lead">
                        Как у операторов ЭДО: исходящие и входящие, статусы
                        доставки и подписи, плюс внутренний кадровый контур.
                    </p>
                    <div className="kedo-feature-grid">
                        {scenarios.map(item => (
                            <article className="kedo-feature" key={item.title}>
                                <div className="kedo-feature-icon">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="kedo-landing-band kedo-landing-band-alt">
                <div className="kedo-landing-inner">
                    <h2>Документы, которые ведут бизнес</h2>
                    <p className="section-lead">
                        Типовые контейнеры и произвольные файлы — с версиями,
                        хэшем SHA-256 и неизменяемым хранением оригинала.
                    </p>
                    <ul className="kedo-doc-types">
                        {docTypes.map(t => (
                            <li key={t.label}>
                                <span className="kedo-doc-type-icon">{t.icon}</span>
                                {t.label}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="kedo-landing-band" id="sign">
                <div className="kedo-landing-inner">
                    <h2>Подпись с юридической силой</h2>
                    <p className="section-lead">
                        Полный стек: ПЭП, УНЭП, УКЭП, МЧД и Госключ — в одном
                        оркестраторе подписания.
                    </p>
                    <div className="kedo-feature-grid">
                        {signWays.map(item => (
                            <article className="kedo-feature" key={item.title}>
                                <div className="kedo-feature-icon">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="kedo-landing-band kedo-landing-band-alt" id="how">
                <div className="kedo-landing-inner">
                    <h2>Как это работает</h2>
                    <p className="section-lead">
                        От загрузки файла до архива и уведомлений во внешние
                        системы — без смены привычного процесса.
                    </p>
                    <ol className="kedo-steps">
                        {steps.map(s => (
                            <li key={s.n}>
                                <span className="kedo-step-icon">{s.icon}</span>
                                <div>
                                    <span className="kedo-step-n">{s.n}</span>
                                    <strong>{s.title}</strong>
                                    <p>{s.text}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section className="kedo-landing-band">
                <div className="kedo-landing-inner kedo-trust">
                    <div>
                        <h2>Безопасность и контроль</h2>
                        <p className="section-lead">
                            Шифрование файлов AES-256-GCM, журнал аудита с
                            хэш-цепочкой, webhook HMAC и роли организации.
                            Соответствие требованиям 63-ФЗ — в регламенте
                            продукта.
                        </p>
                    </div>
                    <ul className="kedo-trust-list">
                        {trustItems.map(item => (
                            <li key={item.label}>
                                <span className="kedo-trust-icon">{item.icon}</span>
                                {item.label}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="kedo-landing-cta">
                <h2>Подключите ЭДО за минуты</h2>
                <p>
                    Создайте организацию, пригласите подписантов и начните обмен
                    документами в Kedo.
                </p>
                <div className="kedo-cta-row">
                    <Link className="kedo-btn kedo-btn-primary" to="/kedo/register">
                        Создать организацию
                    </Link>
                    <a
                        className="kedo-btn kedo-btn-ghost"
                        href="/api/kedo/v1/docs"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ApiOutlined /> Документация API
                    </a>
                </div>
            </section>

            <footer className="kedo-landing-footer">
                <span className="kedo-brand-mark">Kedo</span>
                <span>КЭДО / ЭДО · отдельный продукт</span>
            </footer>
        </div>
    );
};

export default KedoLandingPage;
